// AWARENESS-MVP (docs/design/2026-11-01-companyos-awareness-and-ideas-board.md §1, §3): the
// boards a DE seat has been told to use ("Boards you use"), each with what it is for. The DE's
// platform block and its wake prompt name them by id ("Ideas → <id> (file ideas here)"), so a
// skill files an idea on the board a person chose, never one it guessed.
//
//   GET seats/{id}/boards   owners and admins (the seat drawer's "Boards you use")
//   PUT seats/{id}/boards   owners and admins; people only: the whole set (an omitted board is
//                           no longer used). At most one Ideas board per seat; each board a live
//                           board of this company. Stored on the seat (`settings.boards_you_use`),
//                           so a DE swap keeps it; the seat's version is bumped (`seat.updated`).
//
// Agents never edit it (a structure change is a person's). One Ideas board per content stream,
// shared by the stream's DEs: several seats may name the same board.
import { z } from "zod";
import { Uuid } from "../primitives";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/**
 * What the board is for, which is the routing hint the DE reads: `ideas` "file ideas here",
 * `pieces` "your pieces", `dates` "dates", `other` the note.
 */
export const SeatBoardPurpose = z
  .enum(["ideas", "pieces", "dates", "other"])
  .meta({ id: "SeatBoardPurpose" });
export type SeatBoardPurpose = z.infer<typeof SeatBoardPurpose>;

export const SeatBoardUseInput = z
  .strictObject({
    board_id: Uuid,
    purpose: SeatBoardPurpose,
    /** A short hint shown after the board's name (default: the purpose's own phrase). */
    note: z.string().trim().min(1).max(80).nullable().optional(),
  })
  .meta({ id: "SeatBoardUseInput" });
export type SeatBoardUseInput = z.infer<typeof SeatBoardUseInput>;

export const SeatBoardUse = z
  .object({
    board_id: Uuid,
    board_name: z.string(),
    purpose: SeatBoardPurpose,
    note: z.string().nullable(),
  })
  .meta({ id: "SeatBoardUse" });
export type SeatBoardUse = z.infer<typeof SeatBoardUse>;

export const SeatBoardsView = z
  .object({
    seat_id: Uuid,
    /** The live boards, in the order set (an archived or deleted board is left out). */
    boards: z.array(SeatBoardUse),
    /** The caller may change the set (a person who is an owner or admin). */
    editable: z.boolean(),
  })
  .meta({ id: "SeatBoardsView" });
export type SeatBoardsView = z.infer<typeof SeatBoardsView>;

export const SeatBoardsPut = z
  .strictObject({ boards: z.array(SeatBoardUseInput).max(20) })
  .meta({ id: "SeatBoardsPut" });
export type SeatBoardsPut = z.infer<typeof SeatBoardsPut>;

export const seatBoardsRoutes = {
  "seats.boards": defineRoute({
    ...core,
    id: "seats.boards",
    method: "get",
    path: `${base}/seats/{id}/boards`,
    tag: "org",
    summary: "The boards a seat's DE is told to use, each with what it is for",
    description:
      "Owners and admins. The live boards only (an archived or deleted board is left out). The DE reads the same list in its platform block and wake prompt.",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: SeatBoardsView },
    errors: [],
    emits: [],
  }),
  "seats.boardsUpdate": defineRoute({
    ...core,
    id: "seats.boardsUpdate",
    method: "put",
    path: `${base}/seats/{id}/boards`,
    tag: "org",
    summary: "Set the boards a seat's DE uses (owners and admins, people only)",
    description:
      "The body is the whole set: an omitted board is no longer used. Each board must be a live board of this company (422), named once; at most one `ideas` board per seat (422). Agents and keys are refused (403). The seat's version is bumped and `seat.updated` records the change (`settings.boards_you_use`).",
    access: "manager",
    params: CompanyItemParams,
    body: SeatBoardsPut,
    response: { status: 200, schema: SeatBoardsView },
    errors: [],
    emits: ["seat.updated"],
  }),
} as const;
